import { Grid, Typography, type SxProps, type Theme } from '@mui/material';
import { styled } from '@mui/material';
import type { ImageProps } from 'next/image';
import { localeHasDateFormat } from '../common-utilities';

const Container = styled('div')(({ theme }) => ({
  border: '1px solid #E0E0E0',
  padding: `${theme.spacing(1)}`,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '10px',
}));

export type AssuredBannerProps = {
  bannerState: 'bronze' | 'silver' | undefined;
  imageUrl?: string;
  expiryDate?: Date;
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  locale: string;
};

/**
 * AssuredBanner component from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param {AssuredBannerProps} assuredBannerProps - The props for the AssuredBanner component
 * @returns {ReactElement} The AssuredBanner component
 */
export const AssuredBanner = ({
  bannerState,
  imageUrl,
  expiryDate,
  FlexibleImageComponent,
  locale = 'en-GB',
}: AssuredBannerProps) => {
  let containerStyles: SxProps<Theme> | undefined;
  switch (bannerState) {
    case 'bronze':
      containerStyles = {
        background: '#ad8b51',
      };
      break;
    case 'silver':
      containerStyles = {
        background: '#cdcdcd',
      };
      break;
    default:
      return <></>;
  }

  return (
    <Container sx={containerStyles} data-testid="assured-banner">
      <Grid container>
        <Grid item>
          <FlexibleImageComponent
            loader={() => imageUrl!}
            src={imageUrl!}
            alt=""
            width={100}
            height={100}
            style={{ objectFit: 'contain', display: 'block' }}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ pl: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: 'rgba(18, 15, 15, 0.7)',
              marginBlockStart: 0,
              marginBlockEnd: 0,
            }}
          >
            {bannerState.toUpperCase()}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              color: 'rgba(18, 15, 15, 0.7)',
              marginBlockStart: 0,
              marginBlockEnd: 0,
            }}
          >
            Expiry:{' '}
            {expiryDate
              ? new Date(expiryDate).toLocaleDateString([
                  localeHasDateFormat(locale) ? (locale ?? 'en-GB') : 'en-GB',
                  'en-GB',
                ])
              : ''}
          </Typography>
        </Grid>
      </Grid>
    </Container>
  );
};
